import { Controller } from '@hotwired/stimulus'
import { badgeClasses } from '@/utils/badge_classes'

export default class extends Controller {
  static targets = ['input', 'placeholder', 'preview', 'pendingList', 'dropzone', 'fileList', 'analysisBanner']

  static values = {
    uploadUrl: String,
    notesUrl: String,
    hasAttachments: Boolean,
    refreshUrl: String,
    notifyUpdate: Boolean,
    composeUrl: String
  }

  connect() {
    this.dragCounter = 0
    this.selectedFiles = []
    this.uploadedFileIds = []
  }

  browse(event) {
    event.preventDefault()
    this.inputTarget.click()
  }

  fileSelected(event) {
    const files = Array.from(event.target.files)
    if (files.length > 0) {
      this.selectedFiles = [...this.selectedFiles, ...files]
      this.renderPendingFiles()
    }
  }

  renderPendingFiles() {
    if (this.selectedFiles.length === 0) {
      this.placeholderTarget.classList.remove('d-none')
      this.previewTarget.classList.add('d-none')
      return
    }

    this.placeholderTarget.classList.add('d-none')
    this.previewTarget.classList.remove('d-none')

    this.pendingListTarget.innerHTML = this.selectedFiles
      .map(
        (file, index) =>
          `<div class="bg-light rounded p-2 mb-1" data-file-index="${index}">
            <div class="d-flex align-items-center justify-content-between">
              <div class="d-flex align-items-center">
                <i class="fas fa-file text-muted me-2"></i>
                <small class="text-truncate" style="max-width: 250px;">${file.name}</small>
                <small class="text-muted ms-2">(${this._formatFileSize(file.size)})</small>
              </div>
              <button type="button" class="btn btn-sm btn-link text-danger p-0"
                      data-action="click->financial-package-modal#removePendingFile"
                      data-index="${index}">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <div class="progress mt-1 d-none" style="height: 4px;" data-progress-bar="${index}">
              <div class="progress-bar" role="progressbar" style="width: 0%"></div>
            </div>
          </div>`
      )
      .join('')
  }

  _formatFileSize(bytes) {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / 1048576).toFixed(1)} MB`
  }

  clearFiles(event) {
    event.preventDefault()
    this.inputTarget.value = ''
    this.selectedFiles = []
    this.renderPendingFiles()
  }

  removePendingFile(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this.selectedFiles.splice(index, 1)
    this.inputTarget.value = ''
    this.renderPendingFiles()
  }

  dragover(event) {
    event.preventDefault()
    event.stopPropagation()
  }

  dragenter(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dragCounter++
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.add('dragover')
    }
  }

  dragleave(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dragCounter--
    if (this.dragCounter === 0 && this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove('dragover')
    }
  }

  drop(event) {
    event.preventDefault()
    event.stopPropagation()
    this.dragCounter = 0
    if (this.hasDropzoneTarget) {
      this.dropzoneTarget.classList.remove('dragover')
    }
    const files = Array.from(event.dataTransfer.files)
    if (files.length > 0) {
      this.selectedFiles = [...this.selectedFiles, ...files]
      this.renderPendingFiles()
    }
  }

  // Core upload method — uploads files individually with progress tracking
  async _doUpload() {
    if (this.selectedFiles.length === 0) return []

    // Show all progress bars
    this.selectedFiles.forEach((_, index) => {
      const progressEl = this.pendingListTarget.querySelector(`[data-progress-bar="${index}"]`)
      if (progressEl) progressEl.classList.remove('d-none')

      // Hide remove buttons during upload
      const row = this.pendingListTarget.querySelector(`[data-file-index="${index}"]`)
      const removeBtn = row?.querySelector('button')
      if (removeBtn) removeBtn.classList.add('d-none')
    })

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

    const allNewIds = []

    for (let i = 0; i < this.selectedFiles.length; i++) {
      const file = this.selectedFiles[i]
      const formData = new FormData()
      formData.append('files[]', file)

      try {
        const ids = await this._uploadWithProgress(formData, csrfToken, i)
        allNewIds.push(...ids)
      } catch (error) {
        console.error(`Upload failed for ${file.name}:`, error)
        this._setFileError(i)
      }
    }

    this.uploadedFileIds = [...this.uploadedFileIds, ...allNewIds]
    this.selectedFiles = []
    return allNewIds
  }

  _uploadWithProgress(formData, csrfToken, fileIndex) {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100)
          this._updateProgress(fileIndex, percent)
        }
      })

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          this._updateProgress(fileIndex, 100)
          this._setFileComplete(fileIndex)
          try {
            const data = JSON.parse(xhr.responseText)
            if (data.analysis_status === 'analyzing') {
              this._showAnalysisBanner()
            }
            resolve(data.storage_file_ids || [])
          } catch {
            resolve([])
          }
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`))
        }
      })

      xhr.addEventListener('error', () => reject(new Error('Network error')))

      xhr.open('POST', this.uploadUrlValue)
      xhr.setRequestHeader('X-CSRF-Token', csrfToken)
      xhr.send(formData)
    })
  }

  _updateProgress(fileIndex, percent) {
    const progressEl = this.pendingListTarget.querySelector(`[data-progress-bar="${fileIndex}"]`)
    if (!progressEl) return

    const bar = progressEl.querySelector('.progress-bar')
    if (bar) bar.style.width = `${percent}%`
  }

  _setFileComplete(fileIndex) {
    const row = this.pendingListTarget.querySelector(`[data-file-index="${fileIndex}"]`)
    if (!row) return

    const icon = row.querySelector('.fa-file')
    if (icon) {
      icon.classList.remove('fa-file', 'text-muted')
      icon.classList.add('fa-check-circle', 'text-success')
    }

    const progressEl = row.querySelector(`[data-progress-bar="${fileIndex}"]`)
    if (progressEl) {
      const bar = progressEl.querySelector('.progress-bar')
      if (bar) bar.classList.add('bg-success')
    }
  }

  _setFileError(fileIndex) {
    const row = this.pendingListTarget.querySelector(`[data-file-index="${fileIndex}"]`)
    if (!row) return

    const icon = row.querySelector('.fa-file')
    if (icon) {
      icon.classList.remove('fa-file', 'text-muted')
      icon.classList.add('fa-exclamation-circle', 'text-danger')
    }

    const progressEl = row.querySelector(`[data-progress-bar="${fileIndex}"]`)
    if (progressEl) {
      const bar = progressEl.querySelector('.progress-bar')
      if (bar) bar.classList.add('bg-danger')
    }
  }

  closeModal() {
    const modal = document.getElementById('updated-modal')
    if (modal) bootstrap.Modal.getOrCreateInstance(modal).hide()
  }

  // Save files only (no email), then close modal
  async saveOnly(event) {
    event.preventDefault()

    const button = event.currentTarget
    button.disabled = true
    button.innerHTML = '<i class="fas fa-spinner fa-spin me-1"></i>Saving...'

    try {
      if (this.selectedFiles.length > 0) {
        await this._doUpload()
      }
      this.closeModal()
    } catch (error) {
      console.error('Save failed:', error)
      button.disabled = false
      button.innerHTML = '<i class="fas fa-save me-1"></i>Save Only'
    }
  }

  // Upload files, then navigate to the email compose modal
  async proceedToEmail(event) {
    event.preventDefault()

    const hasFiles =
      this.hasAttachmentsValue || this.selectedFiles.length > 0 || this.uploadedFileIds.length > 0

    if (
      !hasFiles &&
      !confirm('No financial package has been uploaded. Are you sure you want to proceed?')
    ) {
      return
    }

    const button = event.currentTarget
    button.disabled = true
    button.innerHTML = '<i class="fas fa-spinner fa-spin me-1"></i>Loading...'

    try {
      // Upload any pending files first
      if (this.selectedFiles.length > 0) {
        await this._doUpload()
      }

      // Fetch the compose modal and replace modal content
      const response = await fetch(this.composeUrlValue, {
        headers: {
          Accept: 'text/html',
          'Turbo-Frame': 'updated-modal-content'
        }
      })

      if (response.ok) {
        const html = await response.text()
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')
        const frame = doc.querySelector('turbo-frame#updated-modal-content')
        if (frame) {
          const currentFrame = document.querySelector('turbo-frame#updated-modal-content')
          if (currentFrame) {
            currentFrame.innerHTML = frame.innerHTML
          }
        }
      } else {
        console.error('Failed to load compose modal:', response.status)
        button.disabled = false
        button.innerHTML = 'Save & Compose Email <i class="fas fa-arrow-right ms-1"></i>'
      }
    } catch (error) {
      console.error('Failed to load compose modal:', error)
      button.disabled = false
      button.innerHTML = 'Save & Compose Email <i class="fas fa-arrow-right ms-1"></i>'
    }
  }

  _showAnalysisBanner() {
    if (this.hasAnalysisBannerTarget) {
      this.analysisBannerTarget.classList.remove('d-none')
      return
    }

    // If the banner doesn't exist yet (first upload), inject it before the upload area.
    const uploadArea = this.element.querySelector('.dropzone-container')?.closest('.mb-3')
    if (!uploadArea) return

    const banner = document.createElement('div')
    banner.id = 'fp-analysis-status-banner'
    banner.setAttribute('data-financial-package-modal-target', 'analysisBanner')
    banner.className = 'bg-primary bg-opacity-10 text-primary d-flex align-items-center gap-2 rounded px-3 py-2 mb-3 small'
    banner.innerHTML = `
      <span class="spinner-border spinner-border-sm flex-shrink-0" role="status" aria-hidden="true"></span>
      <span>
        <span class="${badgeClasses({ variant: 'primary', size: 'sm', extra: 'me-1' })}" style="letter-spacing:0.04em;">BOLT</span>
        Analyzing document — insights will be ready on the next step.
      </span>`
    uploadArea.parentNode.insertBefore(banner, uploadArea)
  }

  async refreshModal() {
    const response = await fetch(this.refreshUrlValue, {
      headers: {
        Accept: 'text/html',
        'Turbo-Frame': 'updated-modal-content'
      }
    })

    if (response.ok) {
      const html = await response.text()
      const parser = new DOMParser()
      const doc = parser.parseFromString(html, 'text/html')
      const frame = doc.querySelector('turbo-frame#updated-modal-content')
      if (frame) {
        const currentFrame = document.querySelector('turbo-frame#updated-modal-content')
        if (currentFrame) {
          currentFrame.innerHTML = frame.innerHTML
        }
      }
    }
  }
}
